import { z } from 'zod';
import type { ParseResult, SchemaIssue } from '../voyage/schemas.js';
import { PORT_CODE_PATTERN } from './catalog.js';

/**
 * Şirketin mesafe tablosu (ADR 0048 §3): çift başına tek güncel satır, deniz mesafesi simetrik
 * (`portA < portB`). Kaynak önceliği: elle > içe aktarılan > saklanan hesap > o an hesaplanan.
 */
export const PORT_DISTANCE_SOURCES = ['manual', 'imported', 'computed'] as const;
export type PortDistanceSource = (typeof PORT_DISTANCE_SOURCES)[number];

const SOURCE_RANK: Record<PortDistanceSource, number> = { manual: 0, imported: 1, computed: 2 };

/** Tek bacakta en çok mesafe (nm): dünyanın yarı çevresinden uzun deniz yolu yok sayılır. */
export const MAX_DISTANCE_NM = 30_000;
/** CSV içe aktarımda en çok satır. */
export const MAX_DISTANCE_IMPORT_ROWS = 5_000;

/** Çiftin sıralı hali (simetrik anahtar). */
export function portPair(a: string, b: string): [string, string] {
  return a <= b ? [a, b] : [b, a];
}

export const pairKey = (a: string, b: string) => portPair(a, b).join('|');

/** Yeni kaynak eskisinin yerine geçebilir mi: zayıf kaynak güçlüyü ezemez. */
export function canReplaceDistance(
  previous: PortDistanceSource,
  next: PortDistanceSource,
): boolean {
  return SOURCE_RANK[next] <= SOURCE_RANK[previous];
}

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const portCode = () =>
  z.string(typed('INVALID_PORT_CODE')).regex(PORT_CODE_PATTERN, 'INVALID_PORT_CODE');
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) => value.normalize('NFC').trim())
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

export const portDistanceSchema = z
  .object({
    portA: portCode(),
    portB: portCode(),
    distanceNm: z
      .number(typed('INVALID_NUMBER'))
      .gt(0, 'OUT_OF_RANGE')
      .max(MAX_DISTANCE_NM, 'OUT_OF_RANGE'),
    source: z.enum(PORT_DISTANCE_SOURCES, typed('INVALID_OPTION')),
    /** Hesaplanan değerin ağ sürümü (`@felix/sea-data` `MARNET.source`). */
    networkSource: optionalText(120),
    note: optionalText(300),
  })
  .superRefine((value, ctx) => {
    if (value.portA === value.portB) {
      ctx.addIssue({ code: 'custom', message: 'SAME_PORT', path: ['portB'] });
    }
    if (value.source === 'computed' && value.networkSource === null) {
      ctx.addIssue({ code: 'custom', message: 'REQUIRED', path: ['networkSource'] });
    }
  })
  .transform((value) => {
    const [portA, portB] = portPair(value.portA, value.portB);
    return { ...value, portA, portB };
  });
export type PortDistance = z.output<typeof portDistanceSchema>;

function issuesOf(error: z.ZodError): SchemaIssue[] {
  return error.issues.map((issue) => ({
    path: issue.path.map(String).join('.'),
    code: issue.message,
  }));
}

export function parsePortDistance(input: unknown): ParseResult<PortDistance> {
  const result = portDistanceSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: issuesOf(result.error) };
}

export interface LegDistance {
  distanceNm: number | null;
  source: PortDistanceSource | null;
  /** Şirket tablosundan mı (saklanmış), yoksa o an mı hesaplandı. */
  stored: boolean;
}

/** Bacağın mesafesi: şirket tablosundaki değer önce, yoksa o an hesaplanan (saklanmamış). */
export function resolveLegDistance(
  stored: { distanceNm: number; source: PortDistanceSource } | undefined,
  compute: () => number | null,
): LegDistance {
  if (stored) return { distanceNm: stored.distanceNm, source: stored.source, stored: true };
  const computed = compute();
  return computed === null
    ? { distanceNm: null, source: null, stored: false }
    : { distanceNm: computed, source: 'computed', stored: false };
}

export interface DistanceCsvRow {
  line: number;
  portA: string;
  portB: string;
  distanceNm: number;
  note: string | null;
}

export interface DistanceCsvIssue {
  /** Dosyadaki satır (1'den); dosyanın tamamı için 0. */
  line: number;
  code: string;
}

const normalizeCode = (value: string) => value.replace(/\s/g, '').toUpperCase();

/** Basit CSV alanı ayırıcı: tırnaklı alanlar desteklenir. */
function splitCsvLine(line: string, delimiter: string): string[] {
  const fields: string[] = [];
  let current = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else current += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) {
      fields.push(current);
      current = '';
    } else current += ch;
  }
  fields.push(current);
  return fields.map((field) => field.trim());
}

function detectDelimiter(line: string): string {
  if (line.includes('\t')) return '\t';
  if (line.includes(';')) return ';';
  return ',';
}

/**
 * Şirketin kendi lisanslı tablosundan içe aktarım (ADR 0048 §3): `from,to,distance_nm[,note]`.
 * Ayırıcı virgül, noktalı virgül ya da sekme; başlık satırı isteğe bağlı; noktalı virgüllü ya da
 * sekmeli dosyada ondalık virgül kabul edilir. Kodların listede olup olmadığını çağıran denetler.
 */
export function parseDistanceCsv(text: string): {
  rows: DistanceCsvRow[];
  issues: DistanceCsvIssue[];
} {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/);
  const content = lines
    .map((raw, index) => ({ raw, line: index + 1 }))
    .filter((item) => item.raw.trim() !== '');
  if (content.length === 0) return { rows: [], issues: [{ line: 0, code: 'EMPTY_FILE' }] };
  const delimiter = detectDelimiter(content[0]?.raw ?? '');
  const parseNumber = (value: string) => {
    const normalized = delimiter === ',' ? value : value.replace(',', '.');
    return /^\d+(\.\d+)?$/.test(normalized) ? Number(normalized) : Number.NaN;
  };
  const first = splitCsvLine(content[0]?.raw ?? '', delimiter);
  const hasHeader = Number.isNaN(parseNumber(first[2] ?? ''));
  const data = hasHeader ? content.slice(1) : content;
  if (data.length > MAX_DISTANCE_IMPORT_ROWS) {
    return { rows: [], issues: [{ line: 0, code: 'TOO_MANY_ROWS' }] };
  }
  const rows: DistanceCsvRow[] = [];
  const issues: DistanceCsvIssue[] = [];
  const seen = new Set<string>();
  for (const { raw, line } of data) {
    const [from = '', to = '', distance = '', note = ''] = splitCsvLine(raw, delimiter);
    const a = normalizeCode(from);
    const b = normalizeCode(to);
    if (!PORT_CODE_PATTERN.test(a) || !PORT_CODE_PATTERN.test(b)) {
      issues.push({ line, code: 'INVALID_PORT_CODE' });
      continue;
    }
    if (a === b) {
      issues.push({ line, code: 'SAME_PORT' });
      continue;
    }
    const nm = parseNumber(distance);
    if (!Number.isFinite(nm) || nm <= 0 || nm > MAX_DISTANCE_NM) {
      issues.push({ line, code: 'INVALID_DISTANCE' });
      continue;
    }
    const [portA, portB] = portPair(a, b);
    const key = `${portA}|${portB}`;
    if (seen.has(key)) {
      issues.push({ line, code: 'DUPLICATE_PAIR' });
      continue;
    }
    seen.add(key);
    rows.push({ line, portA, portB, distanceNm: nm, note: note.trim().slice(0, 300) || null });
  }
  return { rows, issues };
}

export interface ExistingDistance {
  id: string;
  distanceNm: number;
  source: PortDistanceSource;
  rowVersion: number;
}

/**
 * İçe aktarım planı: yeni çift eklenir; hesaplanan ya da içe aktarılan satır güncellenir; elle
 * girilen satır korunur (`MANUAL_KEPT`); değer ve kaynak aynıysa atlanır (`UNCHANGED`).
 */
export function planDistanceImport(
  rows: readonly DistanceCsvRow[],
  existing: ReadonlyMap<string, ExistingDistance>,
) {
  const insert: DistanceCsvRow[] = [];
  const update: { row: DistanceCsvRow; existing: ExistingDistance }[] = [];
  const skipped: { line: number; reason: 'MANUAL_KEPT' | 'UNCHANGED' }[] = [];
  for (const row of rows) {
    const current = existing.get(`${row.portA}|${row.portB}`);
    if (!current) insert.push(row);
    else if (!canReplaceDistance(current.source, 'imported')) {
      skipped.push({ line: row.line, reason: 'MANUAL_KEPT' });
    } else if (current.source === 'imported' && current.distanceNm === row.distanceNm) {
      skipped.push({ line: row.line, reason: 'UNCHANGED' });
    } else update.push({ row, existing: current });
  }
  return { insert, update, skipped };
}
